'use client';

/**
 * TEMPORARY Seq instrumentation for the teams-gallery horizontal-scroll jank
 * investigation (2026-08-04). Every event carries the same instanceId so the
 * full timeline joins in one Seq filter:
 *
 *   instanceId = 'gallery-scroll-jank--h-scroll--2026-08-04'
 *
 * Hypotheses under test (each event names the one it feeds):
 *   H1 network  - a large API response lands and is JSON-parsed on the main
 *                 thread during the scroll gesture
 *   H2 lanes    - card-boundary crossings commit React state mid-fling
 *   H3 longtask - main-thread tasks >50ms overlap the gesture window
 *   H4 settle   - settle-driven warm-mount work lands while still moving
 *
 * Remove after the investigation closes (both /seq removal conditions met).
 */

import { LogLevel } from '@cabal/shared/util/seq-log';
import { emitSeqLog } from '@/hooks/telemetry/seq-log-transport';

export const GALLERY_JANK_INSTANCE_ID =
  'gallery-scroll-jank--h-scroll--2026-08-04';

const COMPONENT = 'GalleryScrollJank';

enum GalleryJankScope {
  PersistentDebugLog = 'persistent-debug-log',
}

export enum GalleryJankHypothesis {
  Network = 'H1-network',
  Lanes = 'H2-lanes',
  LongTask = 'H3-longtask',
  Settle = 'H4-settle',
}

export function galleryJankLog(
  name: string,
  hypothesis: GalleryJankHypothesis,
  payload: Record<string, unknown>,
  level: LogLevel = LogLevel.Info,
): void {
  emitSeqLog({
    msg: name,
    component: COMPONENT,
    context: {
      scope: GalleryJankScope.PersistentDebugLog,
      instanceId: GALLERY_JANK_INSTANCE_ID,
      feature: 'gallery-scroll-jank',
      hypothesis,
    },
    props: {
      ...payload,
      // performance.now() timestamps let events be ordered against each other
      // finer than Seq ingest timestamps allow.
      perfNowMs: Math.round(performance.now() * 10) / 10,
    },
    level,
  });
}

/**
 * H3: observe main-thread long tasks (>50ms by spec) while the gallery is up.
 * Returns a disposer. Long tasks during jank arrive at a few per second, so
 * per-entry logging stays within the transport's batch bounds.
 */
export function observeGalleryLongTasks(): () => void {
  if (typeof PerformanceObserver === 'undefined') return () => undefined;
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      galleryJankLog(
        'long-task',
        GalleryJankHypothesis.LongTask,
        {
          startMs: Math.round(entry.startTime * 10) / 10,
          durationMs: Math.round(entry.duration * 10) / 10,
        },
        LogLevel.Warning,
      );
    }
  });
  try {
    observer.observe({ entryTypes: ['longtask'] });
  } catch {
    // Browser without longtask support: the H3 lane simply stays empty.
    return () => undefined;
  }
  return () => observer.disconnect();
}
